import { useState, useEffect, useRef } from "react";
import Lottie from "lottie-react";
import heroAnimation from "../../assets/recruitment1.json";
import rolesAnimation from "../../assets/recruitment2.json";

const roles = [
  {
    icon: "🛡️",
    title: "Admin",
    desc: "Total control over user access. Approve pending registrations and oversee the entire R-Bot ecosystem.",
    bg: "rgba(99, 102, 241, 0.1)",
    border: "rgba(99, 102, 241, 0.2)",
  },
  {
    icon: "📈",
    title: "Manager",
    desc: "Oversee recruiter performance and pipeline health across every role with real-time analytics and reporting.",
    bg: "rgba(236, 72, 153, 0.1)",
    border: "rgba(236, 72, 153, 0.2)",
  },
  {
    icon: "📋",
    title: "Hiring Manager",
    desc: "Create JDs, review candidate profiles accepted by recruiters, and make the final selection call.",
    bg: "rgba(16, 185, 129, 0.1)",
    border: "rgba(16, 185, 129, 0.2)",
  },
  {
    icon: "👤",
    title: "Recruiter",
    desc: "Source candidates with AI, manually evaluate resumes, send screening tests, and schedule interviews.",
    bg: "rgba(14, 165, 233, 0.1)",
    border: "rgba(14, 165, 233, 0.2)",
  },
  {
    icon: "🗣️",
    title: "Interviewer",
    desc: "View assigned JDs, join virtual meetings, and provide structured feedback with ratings.",
    bg: "rgba(245, 158, 11, 0.1)",
    border: "rgba(245, 158, 11, 0.2)",
  },
  {
    icon: "🤝",
    title: "Human Resource",
    desc: "Manage onboarding for new hires and offboarding for departing employees, keeping every transition smooth.",
    bg: "rgba(139, 92, 246, 0.1)",
    border: "rgba(139, 92, 246, 0.2)",
  },
];

const pipelineSteps = [
  { num: "01", title: "Smart JD Creation", desc: "Hiring Managers create JDs; R-Bot generates embeddings for semantic matching." },
  { num: "02", title: "Recruiter Sourcing", desc: "Recruiters search the pool, manually evaluate resumes, and accept top talent." },
  { num: "03", title: "Automated Screening", desc: "Test results are sent via email instantly. Move passed candidates to interview." },
  { num: "04", title: "Interview Cycles", desc: "Schedule rounds seamlessly. Interviewers provide real-time ratings and feedback." },
  { num: "05", title: "Final Selection", desc: "HM reviews the full pipeline and completes the hiring process." },
];

const features = [
  { icon: "🤖", title: "Semantic AI Search", desc: "Search the resume pool based on context, not just keywords." },
  { icon: "💻", title: "Screening Tests", desc: "Automated testing with pass/fail notifications sent straight to your inbox." },
  { icon: "🌐", title: "Domain Training", desc: "Upload 50+ resumes to train R-Bot on a new industry instantly." },
];

const LOGIN_URL = "/login";
const REGISTER_URL = "/register";

// Computes the (x, y) offset from center for a card at `index` out of `total`,
// evenly spaced around a circle of `radius` px, starting at the top (12 o'clock).
const getOrbitPosition = (index, total, radius = 380) => {
  const angle = (index * (360 / total) - 90) * (Math.PI / 180);
  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
  };
};

// Fires once when the ref'd element scrolls into view, then stops observing.
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.unobserve(el);
        }
      },
      { threshold, rootMargin: "0px 0px -80px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return [ref, inView];
}

// Eases a number from 0 to `target` once `trigger` becomes true.
function useCountUp(target, trigger, duration = 1200) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!trigger) return;
    let raf;
    let start = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [trigger, target, duration]);

  return value;
}

export default function RBotWelcome() {
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(false);

  const [rolesRef, rolesInView] = useReveal(0.1);
  const [featuresRef, featuresInView] = useReveal(0.15);
  const [pipelineRef, pipelineInView] = useReveal(0.1);
  const [ctaRef, ctaInView] = useReveal(0.2);

  const rolesCount = useCountUp(6, visible);
  const stagesCount = useCountUp(5, visible);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    setTimeout(() => setVisible(true), 100);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (url) => (window.location.href = url);

  return (
    <>
      <style>{`
        /* Import clean, modern fonts */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Outfit:wght@500;700;800&display=swap');

        * { margin: 0; padding: 0; box-sizing: border-box; }

        body {
          font-family: 'Inter', sans-serif;
          background: #0B0E14;
          color: #EDF2F7;
        }

        .rbot-page {
          min-height: 100vh;
          background: #0B0E14;
          position: relative;
          overflow-x: hidden;
        }

        /* Subtle background grid */
        .rbot-bg-grid {
          position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background-image: radial-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px);
          background-size: 40px 40px;
        }

        .rbot-orb {
          position: fixed; border-radius: 50%;
          filter: blur(120px); pointer-events: none; z-index: 0;
          opacity: 0.4;
        }
        .rbot-orb.orb-a { animation: orbFloatA 16s ease-in-out infinite alternate; }
        .rbot-orb.orb-b { animation: orbFloatB 20s ease-in-out infinite alternate; }

        @keyframes orbFloatA {
          from { transform: translate(0, 0); }
          to   { transform: translate(-40px, 50px); }
        }
        @keyframes orbFloatB {
          from { transform: translate(0, 0); }
          to   { transform: translate(30px, -40px); }
        }

        @keyframes navFadeIn {
          from { opacity: 0; transform: translateY(-14px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .rbot-nav {
          position: sticky; top: 0; z-index: 100;
          padding: 1.5rem 4rem;
          display: flex; align-items: center; justify-content: space-between;
          transition: all 0.3s ease;
          animation: navFadeIn 0.7s cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .rbot-nav.scrolled {
          background: rgba(11, 14, 20, 0.85);
          backdrop-filter: blur(12px);
          padding: 1rem 4rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .logo-text {
          font-family: 'Outfit', sans-serif; font-weight: 800;
          font-size: 1.6rem; color: #fff; letter-spacing: -0.02em;
          transition: opacity 0.2s ease;
        }
        .logo-text:hover { opacity: 0.85; }
        .logo-text span { color: #6366F1; }

        .btn-login {
          padding: 10px 24px; border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          background: transparent; color: #fff;
          font-size: 14px; font-weight: 500; cursor: pointer; transition: 0.2s;
        }
        .btn-login:hover { background: rgba(255, 255, 255, 0.05); }

        .btn-register {
          padding: 10px 24px; border-radius: 10px; border: none;
          background: #6366F1; color: #fff;
          font-size: 14px; font-weight: 600;
          cursor: pointer; transition: 0.2s;
        }
        .btn-register:hover { background: #4F46E5; transform: translateY(-1px); }

        .rbot-content { position: relative; z-index: 1; max-width: 1200px; margin: 0 auto; padding: 0 2rem; }

        .hero {
          padding: 10rem 0 6rem;
          display: flex; align-items: center; justify-content: space-between;
          gap: 100px; flex-wrap: wrap;
        }

        .hero-h1 {
          font-family: 'Outfit', sans-serif; font-weight: 800;
          font-size: clamp(3rem, 7vw, 5rem);
          line-height: 1.02; letter-spacing: -0.04em;
          margin-bottom: 1.5rem;
        }
        .hero-h1 .gradient-word {
          background: linear-gradient(135deg, #6366F1 0%, #0EA5E9 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-sub {
          max-width: 650px; margin: 0 0 2.5rem;
          font-size: 1.15rem; color: #94A3B8; line-height: 1.6;
        }

        .hero-stats {
          display: flex; gap: 2.5rem; margin-top: 2.5rem;
          flex-wrap: wrap;
        }
        .hero-stat-num {
          font-family: 'Outfit', sans-serif; font-weight: 800;
          font-size: 1.8rem; color: #fff; line-height: 1;
          font-variant-numeric: tabular-nums;
        }
        .hero-stat-label {
          font-size: 0.8rem; color: #64748B; margin-top: 4px;
          text-transform: uppercase; letter-spacing: 0.05em;
        }

        /* Staggered entrance wrapper: applied to a plain div around each hero
           element, never to the interactive element itself, so hover states
           on buttons stay independent of the entrance transition. */
        .anim-el {
          opacity: 0;
          transform: translateY(18px);
          transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .anim-el.in { opacity: 1; transform: translateY(0); }

        .cta-primary {
          padding: 18px 48px; border-radius: 12px; border: none;
          background: #6366F1; color: #fff;
          font-family: 'Inter', sans-serif; font-size: 16px; font-weight: 700;
          cursor: pointer; transition: 0.3s;
          box-shadow: 0 20px 40px rgba(99, 102, 241, 0.2);
        }
        .cta-primary:hover { transform: translateY(-3px); box-shadow: 0 25px 50px rgba(99, 102, 241, 0.4); }

        .section-title { font-family: 'Outfit', sans-serif; font-size: 2.5rem; font-weight: 700; margin-bottom: 3.5rem; text-align: center; }

        .section-eyebrow {
          text-align: center;
          font-size: 0.8rem; font-weight: 700;
          color: #6366F1; text-transform: uppercase;
          letter-spacing: 0.12em; margin-bottom: 0.75rem;
        }

        /* Generic scroll-reveal: fades a section's content up once it enters
           the viewport. One orchestrated moment per section, not per element. */
        .reveal {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal.in-view { opacity: 1; transform: translateY(0); }

        .stagger-item {
          opacity: 0;
          transform: translateY(22px);
          transition: opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
          transition-delay: var(--d, 0s);
        }
        .reveal.in-view .stagger-item { opacity: 1; transform: translateY(0); }

        .role-icon { font-size: 28px; margin-bottom: 1.5rem; padding: 12px; border-radius: 12px; display: inline-block; }

        /* Roles orbit layout */
        .roles-orbit {
          position: relative;
          width: 100%;
          max-width: 1040px;
          height: 980px;
          margin: 0 auto;
        }

        .roles-orbit-glow {
          position: absolute;
          top: 50%; left: 50%;
          width: 520px; height: 520px;
          background: radial-gradient(circle, rgba(99, 102, 241, 0.18), transparent 70%);
          transform: translate(-50%, -50%);
          filter: blur(40px);
          z-index: 1;
          pointer-events: none;
          animation: glowPulse 7s ease-in-out infinite alternate;
        }
        @keyframes glowPulse {
          from { opacity: 0.6; transform: translate(-50%, -50%) scale(0.94); }
          to   { opacity: 1; transform: translate(-50%, -50%) scale(1.04); }
        }

        .roles-orbit-ring {
          position: absolute;
          top: 50%; left: 50%;
          width: 720px; height: 720px;
          border-radius: 50%;
          border: 1px dashed rgba(99, 102, 241, 0.15);
          transform: translate(-50%, -50%) rotate(0deg);
          transform-origin: center;
          pointer-events: none;
          opacity: 0;
          transition: opacity 1s ease 0.2s;
          animation: orbitSpin 100s linear infinite;
        }
        .roles-orbit.in-view .roles-orbit-ring { opacity: 1; }

        @keyframes orbitSpin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to   { transform: translate(-50%, -50%) rotate(360deg); }
        }

        .roles-orbit-center {
          position: absolute;
          top: 50%; left: 50%;
          width: 360px; height: 360px;
          transform: translate(-50%, -50%) scale(0.85);
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.8s ease 0.1s, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.1s;
        }
        .roles-orbit.in-view .roles-orbit-center {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }

        .role-orbit-card {
          position: absolute;
          top: 50%; left: 50%;
          width: 220px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 20px;
          padding: 1.4rem;
          text-align: left;
          z-index: 2;
          transition: border-color 0.3s ease, background 0.3s ease,
                      opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
          transition-delay: 0s, 0s, var(--d, 0s), var(--d, 0s);
        }

        .role-orbit-card:hover {
          border-color: rgba(99, 102, 241, 0.4);
          background: rgba(255, 255, 255, 0.05);
        }

        @media (max-width: 1080px) {
          .roles-orbit { max-width: 720px; height: 760px; }
          .roles-orbit-ring { width: 560px; height: 560px; }
          .roles-orbit-center { width: 260px; height: 260px; }
          .role-orbit-card { width: 190px; }
        }

        @media (max-width: 900px) {
          .roles-orbit {
            height: auto;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
          }
          .roles-orbit-glow { display: none; }
          .roles-orbit-ring { display: none; }
          .roles-orbit-center {
            position: static;
            transform: none !important;
            opacity: 1 !important;
            width: 220px; height: 220px;
            margin-bottom: 1.5rem;
          }
          .role-orbit-card {
            position: static;
            transform: none !important;
            opacity: 1 !important;
            width: 100%;
            max-width: 420px;
          }
        }

        .pipe-step {
          padding: 2rem; background: rgba(255, 255, 255, 0.02); border-radius: 24px;
          border: 1px solid rgba(255, 255, 255, 0.04); flex: 1;
          position: relative;
          min-width: 200px;
          transition: border-color 0.3s ease, background 0.3s ease;
        }
        .pipe-step:hover {
          border-color: rgba(99, 102, 241, 0.25);
          background: rgba(255, 255, 255, 0.035);
        }
        .pipe-step:not(:last-child)::after {
          content: '→';
          position: absolute;
          right: -26px; top: 2rem;
          color: #6366F1;
          font-size: 1.2rem;
          opacity: 0.5;
        }
        @media (max-width: 900px) {
          .pipe-step:not(:last-child)::after { display: none; }
        }

        .cta-footer {
          margin: 6rem 0; padding: 6rem 3rem; border-radius: 40px;
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(14, 165, 233, 0.05));
          border: 1px solid rgba(99, 102, 241, 0.15); text-align: center;
        }

        @media (max-width: 768px) {
          .rbot-nav { padding: 1rem 2rem; }
          .hero-h1 { font-size: 2.8rem; }
        }

        @media (max-width: 900px) {
          .hero { flex-direction: column; text-align: center; }
          .hero-sub { margin: 0 auto 2.5rem; }
        }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.001ms !important;
            scroll-behavior: auto !important;
          }
        }
      `}</style>

      <div className="rbot-page">
        <div className="rbot-bg-grid" />
        <div className="rbot-orb orb-a" style={{ width: 700, height: 700, background: "#6366F1", top: -300, right: -200 }} />
        <div className="rbot-orb orb-b" style={{ width: 600, height: 600, background: "#0EA5E9", bottom: -200, left: -100 }} />

        <nav className={`rbot-nav ${scrolled ? "scrolled" : ""}`}>
          <div className="logo-text" onClick={() => goTo(LOGIN_URL)} style={{ cursor: 'pointer' }}>
            R<span>BOT</span>
          </div>
          <div style={{ display: 'flex', gap: '16px' }}>
            <button className="btn-login" onClick={() => goTo(LOGIN_URL)}>Log In</button>
            <button className="btn-register" onClick={() => goTo(REGISTER_URL)}>Register</button>
          </div>
        </nav>

        <div className="rbot-content">
          <section className="hero">
            <div style={{ flex: 1, minWidth: "350px" }}>
              <div className={`anim-el ${visible ? "in" : ""}`} style={{ transitionDelay: "0s" }}>
                <h1 className="hero-h1">
                  Hire faster.<br />
                  <span className="gradient-word">With AI precision.</span>
                </h1>
              </div>

              <div className={`anim-el ${visible ? "in" : ""}`} style={{ transitionDelay: "0.12s" }}>
                <p className="hero-sub">
                  R-Bot is an intelligent resourcing ecosystem where your whole team collaborates to find, screen and hire the best talent using semantic search technology.
                </p>
              </div>

              <div className={`anim-el ${visible ? "in" : ""}`} style={{ transitionDelay: "0.24s" }}>
                <button className="cta-primary" onClick={() => goTo(REGISTER_URL)}>
                  Start Your Journey
                </button>
              </div>

              <div className={`anim-el ${visible ? "in" : ""}`} style={{ transitionDelay: "0.36s" }}>
                <div className="hero-stats">
                  <div><div className="hero-stat-num">{rolesCount}</div><div className="hero-stat-label">Team Roles</div></div>
                  <div><div className="hero-stat-num">AI</div><div className="hero-stat-label">Semantic Search</div></div>
                  <div><div className="hero-stat-num">{stagesCount}</div><div className="hero-stat-label">Pipeline Stages</div></div>
                </div>
              </div>
            </div>

            <div
              className={`anim-el ${visible ? "in" : ""}`}
              style={{ flex: 1, display: "flex", justifyContent: "center", transitionDelay: "0.15s" }}
            >
              <Lottie
                animationData={heroAnimation}
                loop
                autoplay
                style={{ width: 500, height: 500 }}
              />
            </div>
          </section>

          <section style={{ padding: '4rem 0' }}>
            <div className="section-eyebrow">Built for Teams</div>
            <h2 className="section-title">The Power of Roles</h2>

            <div ref={rolesRef} className={`roles-orbit ${rolesInView ? "in-view" : ""}`}>
              <div className="roles-orbit-glow" />
              <div className="roles-orbit-ring" />

              <div className="roles-orbit-center">
                <Lottie
                  animationData={rolesAnimation}
                  loop
                  autoplay
                  style={{ width: '130%', height: '130%' }}
                />
              </div>

              {roles.map((r, i) => {
                const { x, y } = getOrbitPosition(i, roles.length);
                const delay = `${i * 0.08}s`;
                return (
                  <div
                    className="role-orbit-card"
                    key={r.title}
                    style={{
                      "--d": delay,
                      opacity: rolesInView ? 1 : 0,
                      transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(${rolesInView ? 1 : 0.85})`,
                    }}
                  >
                    <div
                      className="role-icon"
                      style={{
                        backgroundColor: r.bg,
                        border: `1px solid ${r.border}`
                      }}
                    >
                      {r.icon}
                    </div>

                    <h3 style={{ marginBottom: '8px', fontSize: '1.1rem' }}>
                      {r.title}
                    </h3>

                    <p
                      style={{
                        fontSize: '0.85rem',
                        color: '#94A3B8',
                        lineHeight: '1.6'
                      }}
                    >
                      {r.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          <section style={{ padding: '6rem 0' }}>
            <div className="section-eyebrow">Capabilities</div>
            <h2 className="section-title">What Makes R-Bot Different</h2>
            <div
              ref={featuresRef}
              className={`reveal ${featuresInView ? "in-view" : ""}`}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}
            >
              {features.map((f, i) => (
                <div
                  className="pipe-step stagger-item"
                  key={f.title}
                  style={{ minWidth: 260, "--d": `${i * 0.1}s` }}
                >
                  <div style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>{f.icon}</div>
                  <h4 style={{ marginBottom: '10px', fontSize: '1.1rem' }}>{f.title}</h4>
                  <p style={{ fontSize: '0.9rem', color: '#94A3B8', lineHeight: '1.6' }}>{f.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section style={{ padding: '6rem 0' }}>
            <h2 className="section-title">Workflow Pipeline</h2>
            <div
              ref={pipelineRef}
              className={`reveal ${pipelineInView ? "in-view" : ""}`}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}
            >
              {pipelineSteps.map((s, i) => (
                <div className="pipe-step stagger-item" key={s.num} style={{ "--d": `${i * 0.08}s` }}>
                  <div style={{ color: '#6366F1', fontWeight: '800', marginBottom: '12px', fontSize: '0.9rem' }}>{s.num}</div>
                  <h4 style={{ marginBottom: '10px', fontSize: '1.1rem' }}>{s.title}</h4>
                  <p style={{ fontSize: '0.85rem', color: '#94A3B8', lineHeight: '1.6' }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section ref={ctaRef} className={`cta-footer reveal ${ctaInView ? "in-view" : ""}`}>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '2.2rem', fontWeight: 700, marginBottom: '1rem' }}>
              Ready to transform your hiring?
            </h2>
            <p style={{ color: '#94A3B8', marginBottom: '2rem', fontSize: '1.05rem' }}>
              Join teams already hiring smarter with R-Bot.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="cta-primary" onClick={() => goTo(REGISTER_URL)}>Get Started Free</button>
              <button className="btn-login" onClick={() => goTo(LOGIN_URL)}>Sign In</button>
            </div>
          </section>

          <footer style={{ padding: '3rem 0', textAlign: 'center', color: 'rgba(237, 242, 247, 0.35)', fontSize: '13px', letterSpacing: '0.02em' }}>
           © 2026 R-Bot • Built for Smarter Hiring • Zentree Labs
          </footer>
        </div>
      </div>
    </>
  );
}